import { Injectable } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";
import { EstudioDTO, PanelDTO} from "@DTOS";
import { PanelsService } from "@shared/servicesBackend";
import { CustomState } from "../CustomState";
import { PanelsAcciones } from "./panels.acciones";
import { ClientesAcciones } from "../clientes/clientes.actions";
import { Nilable } from "@shared/utils";
import { EstadoState } from "../EstadoState";
import { AccionEnum } from "../../enums";
import { EstadoEnum } from "../entidades";
import {List} from "immutable";
import {firstValueFrom} from "rxjs";


export interface IPanelState extends EstadoState<PanelDTO> {
    panels: Nilable<List<PanelDTO | null>>;
    total: number;
    estudioViendo: Nilable<EstudioDTO>;
}

@State<IPanelState>({
    name: "panels",
    defaults: {
        panels: null,
        total: 0,
        estadoAccion: null,
        estudioViendo: null
    }
})
@Injectable()
export class PanelState extends CustomState<IPanelState> {
    private panels: Nilable<List<PanelDTO | null>>;
    private cantidadUsada = 0;



    constructor(private panelsService: PanelsService) {
        super();
    }

    @Action(PanelsAcciones.Agregar)
    public async agregarPanel(ctx: StateContext<IPanelState>, action: PanelsAcciones.Agregar) {
        const nuevoState = this.obtenerNuevoState(ctx);

        const panelNuevo = await this.panelsService.agregarPanel(action.panel);
        if (this.panels != null) {
            this.panels.push(panelNuevo);
            nuevoState.panels = this.panels;
        }

        ctx.setState(nuevoState);
    }

    @Action(PanelsAcciones.Obtener)
    public async obtenerPanels(ctx: StateContext<IPanelState>, action: PanelsAcciones.Obtener) {
        const nuevoState = this.obtenerNuevoState(ctx);

        if (action.id !== 0) {
            const panel = await this.panelsService.obtenerPanel(action.id);

        } else {
            if (this.cantidadUsada !== action.cantidad) {
                this.panels = null;
            }
            if (this.panels == null) {
                const paginadoPanels = await firstValueFrom(
                    this.panelsService.obtenerPanels(action.cantidad, action.pagina)
                );
                this.cantidadUsada = action.cantidad;
                nuevoState.total = paginadoPanels.cantidad;
                this.panels = List(paginadoPanels.resultados)
                    .concat(new Array<PanelDTO | null>(paginadoPanels.cantidad - paginadoPanels.resultados.length)
                        .fill(null));
                ctx.dispatch(new ClientesAcciones.PedirEntidades(PanelsAcciones.CargarClientes));
            } else {
                const porcionPanels = this.panels
                    .slice(action.cantidad * (action.pagina - 1), action.pagina * action.cantidad)
                    .filter(panel => panel != null);
                if (porcionPanels.count() < action.pagina * action.cantidad) {
                    const paginadoPanels = await firstValueFrom(
                        this.panelsService.obtenerPanels(action.cantidad, action.pagina)
                    );
                    this.panels = this.panels.slice(0, action.cantidad * (action.pagina - 1))
                        .concat(paginadoPanels.resultados)
                        .concat(this.panels.slice(action.pagina * action.cantidad));
                }
            }
            nuevoState.panels = this.panels
                .slice(action.cantidad * (action.pagina - 1), action.pagina * action.cantidad)
                .toList();
        }

        ctx.setState(nuevoState);
    }

    @Action(PanelsAcciones.Actualizar)
    public async actualizar(ctx: StateContext<IPanelState>, action: PanelsAcciones.Actualizar) {
        const nuevoState = this.obtenerNuevoState(ctx);

        const panelNuevo = await this.panelsService.actualizarPanel(action.panel);
        if (this.panels != null) {
            this.panels = this.panels.map(panel => panel?.id === action.panel.id ? panelNuevo : panel);
            nuevoState.panels = this.panels.toList();
            nuevoState.estadoAccion = {
                accion: AccionEnum.ACTUALIZAR,
                estado: EstadoEnum.CORRECTO
            };
        }

        ctx.setState(nuevoState);
        ctx.dispatch(new ClientesAcciones.PedirEntidades(PanelsAcciones.CargarClientes));
    }

    @Action(PanelsAcciones.CargarClientes)
    public cargarClientes(ctx: StateContext<IPanelState>, action: PanelsAcciones.CargarClientes) {
        const nuevoState = this.obtenerNuevoState(ctx);

        if (this.panels != null) {
            this.panels = this.panels?.map(panel => {
                if (panel == null) {
                    return null;
                }
                const clone = new PanelDTO(panel.obtenerJSON());
                const clientePanel = action.clientes.find(cliente => cliente.id === panel.cliente.id);
                clone.cliente = clientePanel!;
                return clone;
            });
            nuevoState.panels = this.panels.toList();
        }

        ctx.setState(nuevoState);
    }

    @Action(PanelsAcciones.Borrar)
    public async borrar(ctx: StateContext<IPanelState>, action: PanelsAcciones.Borrar) {
        const nuevoState = this.obtenerNuevoState(ctx);

        const borro = await this.panelsService.borrarPanel(action.panel);
        if (this.panels != null && borro) {
            nuevoState.panels = this.panels.filter(panel => panel?.id !== action.panel.id);
        }

        ctx.setState(nuevoState);
    }

    @Action(PanelsAcciones.CargarResultado)
    public async cargarResultado(ctx: StateContext<IPanelState>, action: PanelsAcciones.CargarResultado) {
        const nuevoState = this.obtenerNuevoState(ctx);
        for (let i = 0; i < action.archivos.length; i++) {
            await this.panelsService.subirArchivo(action.panel, action.archivos.item(i)!, action.dashboard );
        }
        nuevoState.estadoAccion = {
            accion: AccionEnum.ACTUALIZAR,
            estado: EstadoEnum.CORRECTO
        };
        ctx.setState(nuevoState);
    }

    @Action(PanelsAcciones.CrearDesdeArchivo)
    public async crearDesdeArchivo(ctx: StateContext<IPanelState>, action: PanelsAcciones.CrearDesdeArchivo) {
        for (let i = 0; i < action.archivos.length; i++) {
            await this.panelsService.crearDesdeArchivo(action.datos, action.clientes, action.archivos.item(i)!);
        }
    }

    @Action(PanelsAcciones.ReiniciarEstado)
    public reiniciarEstado(ctx: StateContext<IPanelState>) {
        const nuevoState = this.obtenerNuevoState(ctx);
        nuevoState.estadoAccion = null;
        ctx.setState(nuevoState);
    }

    @Action(PanelsAcciones.Reiniciar)
    public reiniciar(ctx: StateContext<IPanelState>) {
        const nuevoState = this.obtenerNuevoState(ctx);

        this.panels = List();
        nuevoState.panels = List();

        ctx.setState(nuevoState);
    }
}
